/**
 * Logica pura per mostrare gli allegati delle note (scheda Lead/Cliente).
 * Testata in __tests__/noteAttachmentFiles.test.ts
 */

export type AttachmentKind = 'image' | 'pdf' | 'sheet' | 'doc' | 'slides' | 'archive' | 'code' | 'other';

const EXT_KIND: Record<string, AttachmentKind> = {
  png: 'image', jpg: 'image', jpeg: 'image', gif: 'image', webp: 'image', svg: 'image', heic: 'image',
  pdf: 'pdf',
  xls: 'sheet', xlsx: 'sheet', xlsm: 'sheet', csv: 'sheet', ods: 'sheet', numbers: 'sheet',
  doc: 'doc', docx: 'doc', odt: 'doc', rtf: 'doc', txt: 'doc', pages: 'doc',
  ppt: 'slides', pptx: 'slides', odp: 'slides', key: 'slides',
  zip: 'archive', rar: 'archive', '7z': 'archive', gz: 'archive', tar: 'archive',
  html: 'code', htm: 'code', js: 'code', css: 'code', json: 'code', xml: 'code',
};

/** Estensione in minuscolo senza punto ('' se il nome non ne ha). */
export function attachmentExtension(name: string | null | undefined): string {
  const clean = (name || '').trim();
  const dot = clean.lastIndexOf('.');
  if (dot <= 0 || dot === clean.length - 1) return '';
  return clean.slice(dot + 1).toLowerCase();
}

/**
 * Tipo di file: prima l'estensione (più affidabile, il mime degli Excel arriva spesso
 * come application/octet-stream o application/vnd.openxmlformats...), poi il mime.
 */
export function getAttachmentKind(name: string | null | undefined, mime?: string | null): AttachmentKind {
  const byExt = EXT_KIND[attachmentExtension(name)];
  if (byExt) return byExt;
  const m = (mime || '').toLowerCase();
  if (m.startsWith('image/')) return 'image';
  if (m.includes('pdf')) return 'pdf';
  if (m.includes('spreadsheet') || m.includes('excel') || m.includes('csv')) return 'sheet';
  if (m.includes('presentation') || m.includes('powerpoint')) return 'slides';
  if (m.includes('word') || m.includes('document') || m.startsWith('text/plain')) return 'doc';
  if (m.includes('zip') || m.includes('rar') || m.includes('compressed')) return 'archive';
  if (m.includes('html') || m.includes('javascript') || m.includes('css') || m.includes('json')) return 'code';
  return 'other';
}

/** Dimensione leggibile in italiano (virgola decimale). null se sconosciuta. */
export function formatAttachmentSize(bytes: number | null | undefined): string | null {
  if (bytes === null || bytes === undefined || !Number.isFinite(bytes) || bytes < 0) return null;
  if (bytes < 1024) return `${bytes} B`;
  const fmt = (n: number) => n.toFixed(1).replace('.', ',');
  if (bytes < 1024 * 1024) return `${fmt(bytes / 1024)} KB`;
  return `${fmt(bytes / (1024 * 1024))} MB`;
}

export interface AttachmentLike {
  id: string;
  name: string;
  url: string;
  type?: string;
  size?: number;
  created_at?: string;
}

export interface NoteWithAttachments<A extends AttachmentLike = AttachmentLike> {
  id: string;
  created_at?: string;
  attachments?: A[] | null;
}

export type CollectedAttachment<A extends AttachmentLike = AttachmentLike> = A & {
  noteId: string;
  noteCreatedAt?: string;
};

/**
 * Tutti gli allegati delle note in una lista unica, dal più recente.
 * Dedup per id (la stessa nota può comparire due volte nella cronologia).
 */
export function collectNoteAttachments<A extends AttachmentLike>(
  notes: Array<NoteWithAttachments<A>> | null | undefined,
): Array<CollectedAttachment<A>> {
  const seen = new Set<string>();
  const out: Array<CollectedAttachment<A>> = [];
  for (const note of notes || []) {
    for (const att of note.attachments || []) {
      if (!att?.id || seen.has(att.id)) continue;
      seen.add(att.id);
      out.push({ ...att, noteId: note.id, noteCreatedAt: note.created_at });
    }
  }
  const time = (a: CollectedAttachment<A>) => Date.parse(a.created_at || a.noteCreatedAt || '') || 0;
  return out.sort((a, b) => time(b) - time(a));
}

/**
 * Link "Scarica": l'attributo `download` è ignorato sui link di un altro dominio
 * (lo storage Supabase), quindi per gli URL firmati si aggiunge `download=<nome>`,
 * che lo storage trasforma in Content-Disposition: attachment. Gli altri URL restano uguali.
 */
export function withDownloadParam(url: string | null | undefined, fileName: string): string | null {
  if (!url) return null;
  if (!url.includes('/storage/v1/object/sign/')) return url;
  if (/[?&]download=/.test(url)) return url;
  const sep = url.includes('?') ? '&' : '?';
  return `${url}${sep}download=${encodeURIComponent(fileName || 'file')}`;
}
